import { useQuery } from "@tanstack/react-query";
import {
  getApiProductsById,
  type FileRelationResponse,
  type ProductResponse,
  type SpecificationResponse,
} from "apiClients/base-generated";
import {
  getHeyApiRequestOptions,
  normalizeHeyApiLang,
} from "apiClients/base/heyApiRequestOptions";

export type SupplierSheetFile = {
  id: number;
  name: string;
  url: string;
  thumb?: string;
  extension: string;
  relationType: string;
  /** Estimated size — MediaAsset has no stored byte size today. */
  bytes: number;
  updatedAt?: string;
  kind: "image" | "doc";
  docType: "pdf" | "xlsx" | "other";
};

export type SupplierProductDetail = {
  id: number;
  name: string;
  sku: string;
  ean?: string;
  thumb?: string;
  images: SupplierSheetFile[];
  docs: SupplierSheetFile[];
  specs: { label: string; value: string }[];
};

function heyApiResultToError(err: unknown): Error {
  const message = typeof err === "string" ? err : JSON.stringify(err);
  return new Error(message);
}

function estimateBytes(extension: string, kind: "image" | "doc"): number {
  const ext = extension.replace(/^\./, "").toLowerCase();
  if (kind === "image") {
    return 350_000;
  }
  if (ext === "pdf") {
    return 800_000;
  }
  if (ext === "xlsx" || ext === "xls") {
    return 400_000;
  }
  return 500_000;
}

function resolveDocType(extension: string): SupplierSheetFile["docType"] {
  const ext = extension.replace(/^\./, "").toLowerCase();
  if (ext === "pdf") {
    return "pdf";
  }
  if (ext === "xlsx" || ext === "xls") {
    return "xlsx";
  }
  return "other";
}

function mapFile(
  file: FileRelationResponse,
  kind: "image" | "doc",
): SupplierSheetFile | null {
  if (!file.id) {
    return null;
  }
  const extension = file.extension ?? "";
  return {
    id: file.id,
    name: file.name?.trim() || `fil-${file.id}`,
    url: file.url ?? "",
    thumb: file.previewUrl || file.url || undefined,
    extension,
    relationType: file.relationType ?? "",
    bytes: estimateBytes(extension, kind),
    kind,
    docType: resolveDocType(extension),
  };
}

function mapSpecs(specs: SpecificationResponse[] | null | undefined) {
  return (specs ?? [])
    .map((s) => {
      const label = s.label?.trim() ?? "";
      const unit = s.unit?.unitSymbol || s.unit?.unitName || "";
      const raw = s.value?.trim() ?? "";
      if (!label || !raw) {
        return null;
      }
      return {
        label,
        value: unit ? `${raw} ${unit}` : raw,
      };
    })
    .filter((s): s is { label: string; value: string } => s != null);
}

function mapProduct(product: ProductResponse): SupplierProductDetail {
  const images = (product.images ?? [])
    .map((f) => mapFile(f, "image"))
    .filter((f): f is SupplierSheetFile => f != null);

  const docs = [
    ...(product.manuals ?? []),
    ...(product.safetySheets ?? []),
    ...(product.dimensionalSheets ?? []),
  ]
    .map((f) => mapFile(f, "doc"))
    .filter((f): f is SupplierSheetFile => f != null);

  const mainVariant =
    product.variants?.find((v) => v.itemNo === product.mainItemNo) ??
    product.variants?.[0];

  return {
    id: product.id ?? 0,
    name: product.name?.trim() || "Namnlös produkt",
    sku: product.mainItemNo?.trim() || "—",
    ean: mainVariant?.ean?.trim() || undefined,
    thumb: images[0]?.thumb,
    images,
    docs,
    specs: mapSpecs(product.specifications),
  };
}

export function useSupplierProductDetailQuery(
  productId: number | null,
  locale?: string | null,
) {
  const lang = normalizeHeyApiLang(locale);

  return useQuery({
    queryKey: ["supplier-product-detail", productId, lang ?? "default"],
    enabled: productId != null && productId > 0,
    queryFn: async (): Promise<SupplierProductDetail> => {
      const base = getHeyApiRequestOptions(lang);
      const result = await getApiProductsById({
        ...base,
        path: { id: productId! },
        headers: {
          ...(base as { headers?: Record<string, string> }).headers,
          // API accepts comma-separated includes; generated type is a single union member.
          includes: "MediaAssets,ProductItems,Specifications" as "MediaAssets",
        },
      });

      if (result.error) {
        throw heyApiResultToError(result.error);
      }

      const product = result.data as ProductResponse | undefined;
      if (!product) {
        throw new Error("Produkten hittades inte");
      }

      return mapProduct(product);
    },
  });
}
